<template>
  <b-modal
    v-model="showModal"
    v-bind="extraBinds"
    v-on="$listeners"
    ref="sModal"
    no-close-on-backdrop
    no-close-on-esc
    dialog-class="s-modal"
    :size="modalSize"
    :centered="centered"
    :title="titleText"
    :ok-title="okTitle"
    :cancel-title="cancelTitle"
    :ok-only="okOnly"
    @ok="okProxy()"
    @cancel="cancelProxy()"
    @hidden="onHidden()"
  >
    <template
      v-for="(slotContent, slotName) in $scopedSlots"
      #[slotName]="slotData"
    >
      <slot
        v-bind="slotData"
        :name="slotName"
      />
      <div
        v-if="isLoading"
        v-bind:key="slotName+'a'"
        class="s-modal__loading-overlay"
      ></div>

      <div
        v-bind:key="slotName+'b'"
        class="s-modal__modal-confirm"
      >
        <div
          v-html="confirmMessage"
          class="mb-3"
        ></div>
        <footer class="s-modal__modal-confirm-footer pt-3">
          <div class="s-modal__modal-confirm-btns">
            <b-button
              variant="secondary"
              class="mx-sm-2 mb-2 mb-sm-0"
              @click="cancelConfirm()"
            >
              {{ cancelConfirmTitle }}
            </b-button>
            <b-button
              variant="primary"
              @click="okConfirm()"
            >
              {{ okConfirmTitle }}
            </b-button>
          </div>
        </footer>
      </div>
    </template>

    <template
      v-if="hasPromise"
      slot="modal-footer"
    >
      <s-loading-button
        v-if="!okOnly"
        class="s-modal__btn-loading"
        id="s-modal-btn-cancel-loading"
        variant="secondary"
        :disabled="isCancelDisabled"
        @click="cancelProxy($event)"
      >
        <span v-html="cancelTitleText"></span>
      </s-loading-button>

      <s-loading-button
        class="s-modal__btn-loading"
        id="s-modal-btn-ok-loading"
        variant="primary"
        :disabled="isOkDisabled"
        @click="okProxy($event)"
      >
        <span v-html="okTitleText"></span>
      </s-loading-button>
    </template>

    <template
      v-else
      slot="modal-footer"
    >
      <div :class="{ 'btns-space': spaced }">
        <b-button
          v-if="!okOnly"
          variant="secondary"
          :disabled="isCancelDisabled"
          @click="cancelProxy"
        >
          <span v-html="cancelTitleText"></span>
        </b-button>
        <b-button
          variant="primary"
          :disabled="isOkDisabled"
          @click="okProxy"
        >
          <loading
            v-if="isLoading"
            class="d-inline-block"
          />
          <span v-html="okTitleText"></span>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import translate from '@/services/translate';
import extraBindsMixin from '@/components/mixins/extraBindsMixin';
import ConfirmationTypes from '@/constants/confirmationTypes';
import loading from '@/components/Loading';

export default {
  props: {
    spaced: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
    },
    centered: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: () => translate('general.attention'),
    },
    cancelTitle: {
      type: String,
      default: () => translate('general.cancel'),
    },
    okTitle: {
      type: String,
      default: () => translate('general.ok'),
    },
    cancelTitleHtml: {
      type: String,
    },
    okTitleHtml: {
      type: String,
    },
    okOnly: {
      type: Boolean,
      default: false,
    },
    cancelPromise: {
      type: Function,
    },
    okPromise: {
      type: Function,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    confirmType: {
      type: String,
      default: ConfirmationTypes.OK_CANCEL,
    },
    cancelConfirmMessageCode: {
      type: String,
    },
    okConfirmMessageCode: {
      type: String,
    },
    noCloseHeader: {
      type: Boolean,
      default: false,
    },
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    okDisabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    loading,
  },
  mixins: [
    extraBindsMixin,
  ],
  data() {
    return {
      modalHeightOriginal: '0px',
      confirmTitle: '',
      confirmMessage: '',
      $event: null,
      callbackConfirm: null,
      isConfirming: false,
      overflowListener: null,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    modalSize() {
      return this.isConfirming ? 'md' : this.extraBinds.size || 'md';
    },
    titleText() {
      return this.isConfirming ? this.confirmTitle : this.title;
    },
    cancelTitleText() {
      return this.cancelTitleHtml ? this.cancelTitleHtml : this.cancelTitle;
    },
    okTitleText() {
      return this.okTitleHtml ? this.okTitleHtml : this.okTitle;
    },
    cancelConfirmTitle() {
      return (this.confirmType === ConfirmationTypes.OK_CANCEL
        ? this.$t('general.cancel')
        : this.$t('general.no'));
    },
    okConfirmTitle() {
      return (this.confirmType === ConfirmationTypes.OK_CANCEL
        ? this.$t('general.ok')
        : this.$t('general.yes'));
    },
    hasPromise() {
      return this.okPromise || this.cancelPromise;
    },
    isCancelDisabled() {
      return this.isLoading || this.cancelDisabled;
    },
    isOkDisabled() {
      return this.isLoading || this.okDisabled;
    },
  },
  mounted() {
    this.initElements();
  },
  methods: {
    element() {
      const [element] = document.getElementsByClassName('s-modal');
      return element || document.createElement('div');
    },
    modalBodyElement() {
      return (this.element().querySelectorAll && this.element().querySelectorAll('.modal-body')[0])
        || document.createElement('div');
    },
    modalFooterElement() {
      return (this.element().querySelectorAll && this.element().querySelectorAll('.modal-footer')[0])
        || document.createElement('div');
    },
    modalConfirmElement() {
      return (this.element().querySelectorAll && this.element().querySelectorAll('.s-modal__modal-confirm')[0])
        || document.createElement('div');
    },
    initElements() {
      this.modalHeightOriginal = `${this.modalBodyElement().offsetHeight}px`;
      this.modalBodyElement().style.minHeight = this.modalHeightOriginal;
    },
    clearOverflowListener() {
      if (this.overflowListener) {
        clearTimeout(this.overflowListener);
      }
    },
    onShowConfirm() {
      this.clearOverflowListener();
      this.isConfirming = true;
      this.modalBodyElement().style.minHeight = `${this.modalConfirmElement().offsetHeight}px`;
      this.modalBodyElement().style.overflowX = 'hidden';
      this.modalFooterElement().classList.add('s-modal__modal-footer--hide');
      this.modalBodyElement().classList.add('s-modal__modal-body--hide');
      this.modalConfirmElement().classList.add('s-modal__modal-confirm--show');
    },
    onHideConfirm() {
      this.isConfirming = false;
      this.modalFooterElement().classList.remove('s-modal__modal-footer--hide');
      this.modalBodyElement().classList.remove('s-modal__modal-body--hide');
      this.modalConfirmElement().classList.remove('s-modal__modal-confirm--show');
      this.modalBodyElement().style.minHeight = this.modalHeightOriginal;
      this.clearOverflowListener();
      this.overflowListener = setTimeout(() => {
        this.modalBodyElement().style.overflowX = '';
      }, 400);
    },
    cancelProxy($event) {
      if (this.cancelConfirmMessageCode) {
        this.$emit('toggle-message');
        this.onShowConfirm();
        this.confirmProxy($event, this.cancelConfirmMessageCode, this.resolveCancel);
        return;
      }

      this.resolveCancel($event);
    },
    okProxy($event) {
      if (this.okConfirmMessageCode) {
        this.$emit('toggle-message');
        setTimeout(() => {
          this.onShowConfirm();
        }, 100);
        this.confirmProxy($event, this.okConfirmMessageCode, this.resolveOk);
        return;
      }

      this.resolveOk($event);
    },
    resolveCancel($event) {
      if ($event && this.cancelPromise) {
        $event.wait(this.cancelPromise());
        return;
      }

      this.$emit('cancel');
    },
    resolveOk($event) {
      if ($event && this.okPromise) {
        $event.wait(this.okPromise());
        return;
      }
      this.$emit('ok');
    },
    cancelConfirm() {
      this.$emit('toggle-message');
      this.onHideConfirm();
    },
    okConfirm() {
      this.callbackConfirm(this.$event);
      this.onHideConfirm();
    },
    confirmProxy($event, messageCode, callback) {
      this.$event = $event;
      this.callbackConfirm = callback;
      this.confirmTitle = this.$t(`messages.${messageCode.toUpperCase()}.title`);
      this.confirmMessage = this.$t(`messages.${messageCode.toUpperCase()}.message`);
      this.onShowConfirm();
    },
    onHidden() {
      this.$emit('hide');
      this.onHideConfirm();
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

$transition_1:  height .3s ease-in-out .4s
$transition_2:  transform .3s ease-in-out
$transition_3:  opacity .2s ease-in-out
$transition_4:  visibility .3s ease

.s-modal
  &__loading-overlay
    position: absolute
    top: 0
    right: 0
    bottom: 0
    left: 0
    background-color: var(--color-modal-background)
    opacity: 0.5
    cursor: not-allowed
    z-index: 2000

.btns-space
  display: flex
  justify-content: space-between
  width: 100%

::v-deep
  .modal-body
    position: relative
    overflow: hidden

  .modal-footer > .row,
  .modal-footer button,
  .s-modal__modal-confirm-btns button
    width: 100%

  .s-modal
    &__modal-confirm
      display: flex
      flex-direction: column
      justify-content: space-between
      position: absolute
      top: 0
      right: 9999px
      background-color: var(--color-modal-background)
      border-radius: 4px
      text-align: left
      transition: $transition_1, $transition_2, $transition_3, $transition_4
      transform: translateX(150px)
      padding: 1rem
      height: 0
      visibility: hidden
      opacity: 0
      overflow: hidden
      z-index: 1

      &.s-modal__modal-confirm--show
        position: relative
        top: 0
        right: 0
        bottom: 0
        left: 0
        transform: translateX(0px)
        padding: 0
        height: auto
        visibility: visible
        opacity: 1

    &__modal-confirm-btns
      display: flex
      justify-content: flex-end
      flex-wrap: wrap
      align-items: center

      & button
        margin: 0.25rem

        &:hover
          box-shadow: 0 4px 4px rgba(0, 0, 0, .2)
          border-color: transparent

  .modal-footer:not(.s-modal__modal-footer--hide)
    min-height: 66px
    overflow: hidden
    opacity: 1
    transition: padding .3s ease .6s, opacity .6s ease-in-out .3s, height .3s ease-in-out .4s

  .modal-footer:not(.s-modal__modal-footer--hide) *
    visibility: visible
    transition: opacity .6s ease, visibility .6s ease

  .s-modal__modal-footer--hide
    overflow: hidden
    padding: 0 15px
    height: 0
    visibility: hidden
    opacity: 0
    transition: padding .3s ease, opacity .3s ease, visibility .3s ease, height .3s ease .4s

  .s-modal__modal-footer--hide *
    opacity: 0
    transition: opacity .3s ease

  .modal-body,
  .modal-body > *:not(.s-modal__modal-confirm):not(.s-modal__loading-overlay)
    transform: none
    opacity: 1
    transition: height .3s ease-in-out .5s, transform .3s ease-in-out .1s, opacity .5s ease-in-out

  .modal-body.s-modal__modal-body--hide
    border-radius: 4px

  .modal-body.s-modal__modal-body--hide > *:not(.s-modal__modal-confirm)
    position: absolute
    overflow: hidden
    margin: 0 !important
    padding: 0 !important
    height: 0 !important
    opacity: 0
    visibility: hidden
    transform: translateX(150px)
    z-index: 0

+media-min-sm()
  ::v-deep
    .modal-footer button,
    .s-modal__modal-confirm-btns button
      width: auto

</style>
